import { QueryResult } from '@apollo/client'
import * as React from 'react'
import styled from 'styled-components'
import { clock } from '../../../../common/src/clock'
import { InlineErrorBoundary } from '../../../../common/src/components/InlineErrorBoundary'
import { IntroText, Spacer, H4 } from '../../../../common/src/components/core'
import { groupBy } from '../../../../common/src/func'
import Time from '../../../../common/src/time'
import { FetchAllUsersQueryQuery, FetchUserContextQuery } from '../../../../common/src/types.gen'
import { fetchAllUsersQuery } from '../../graphql/queries/OperatorDashboardQueries'
import OperatorDashboardTable from '../components/OperatorDashboardTable'
import { PollingQuery } from '../components/PollingQuery'

export interface OperatorDashboardSectionProps {
  user: FetchUserContextQuery['self']
}

interface OperatorDashboardSectionState {
  timeNow: string
}

class OperatorDashboardSectionImpl extends React.Component<
  OperatorDashboardSectionProps & QueryResult<FetchAllUsersQueryQuery>,
  OperatorDashboardSectionState
> {
  state = { timeNow: Time.now().toISOString() }

  public componentDidMount() {
    clock.addListener(this.handleTimeChange)
  }

  public componentWillUnmount() {
    clock.removeListener(this.handleTimeChange)
  }

  public render() {
    const { data, user, error } = this.props
    if (data == null || data.allUsers == null) {
      return (
        <Container>
          <IntroText>Loading…</IntroText>
        </Container>
      )
    }

    if (error != null) {
      return (
        <Container>
          <IntroText color="coral">Failed to load operator dashboard.</IntroText>
        </Container>
      )
    }

    const scheduled = this.sortUsers(this.filterUsers(data.allUsers))
    const userGroups = Object.values(
      groupBy(scheduled, s => (s.activeOperatorShift && s.activeOperatorShift.shift.timeStart) + '')
    )

    return (
      <>
        <Center>
          <InlineErrorBoundary componentName="OperatorDashboardTable">
            <Spacer height={16} />
            {this.renderPresentFraction(data.allUsers)}
            <Spacer height={16} />

            {userGroups.map((group, i) => (
              <div key={i}>
                <OperatorDashboardTable
                  showHeader={i === 0} // only show the header for the first table
                  loggedInUser={user}
                  timeNow={this.state.timeNow}
                  users={group}
                />
                <Spacer height={32} />
              </div>
            ))}
          </InlineErrorBoundary>
          <Spacer height={16} />
        </Center>
      </>
    )
  }

  private handleTimeChange = (timeNow: string) => this.setState({ timeNow })

  private renderPresentFraction(users: FetchAllUsersQueryQuery['allUsers']) {
    const shouldBePresent = users.filter(user => user.activeOperatorShift != null).length
    const isPresentCount = users.filter(user => user.isClockedIn && user.activeOperatorShift != null).length

    if (shouldBePresent === isPresentCount) {
      return null // we only want to show this if someone is actually absent
    }

    return <H4 color="midway">{`${shouldBePresent - isPresentCount} missing out of ${shouldBePresent} scheduled`}</H4>
  }

  private computeSortKeyForUser(user: FetchAllUsersQueryQuery['allUsers'][0]): string {
    let sortKey = ''

    sortKey = (user.userType === 'SENIOR_OPERATOR' ? 'a' : 'z') + '_' + sortKey

    if (user.activeOperatorShift && user.autoStationAssignmentEnabled) {
      sortKey = 'z' + sortKey
    } else {
      sortKey = 'zzz_' + sortKey
    }

    if (user.activeOperatorShift) {
      sortKey = user.activeOperatorShift.shift.timeStart + sortKey
    } else {
      sortKey = 'z' + sortKey
    }

    return sortKey
  }

  private sortUsers(users: FetchAllUsersQueryQuery['allUsers']) {
    return users.sort((a: FetchAllUsersQueryQuery['allUsers'][0], b: FetchAllUsersQueryQuery['allUsers'][0]) =>
      this.computeSortKeyForUser(a).localeCompare(this.computeSortKeyForUser(b))
    )
  }

  private filterUsers(users: FetchAllUsersQueryQuery['allUsers']) {
    return users.filter(
      (user: FetchAllUsersQueryQuery['allUsers'][0]) => !!user.activeOperatorShift || user.isClockedIn
    )
  }
}

const Container = styled.div`
  width: 100%;
  display: inline-block;
  min-height: '128px';
`

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex-direction: column;
`

export default function OperatorDashboardSection(props: OperatorDashboardSectionProps) {
  return (
    <PollingQuery query={fetchAllUsersQuery}>
      {(res: QueryResult<FetchAllUsersQueryQuery>) => <OperatorDashboardSectionImpl {...props} {...res} />}
    </PollingQuery>
  )
}
